import type { ComponentType } from 'react';
import {
  LayoutDashboard, Building2, Boxes, PawPrint, Milk, Baby, HeartPulse, TreePalm,
  Warehouse, ShoppingCart, ClipboardList, Droplets, ShieldCheck, QrCode, FileBarChart2,
  MessageSquare, GitBranch, BookOpen, Store,
} from 'lucide-react';
import { ChickenIcon } from '../components/icons/ChickenIcon';

export interface NavItem {
  label: string;
  to: string;
  icon: ComponentType<{ className?: string }>;
}
// note: sidebar renders icons at 18px via the `[&_svg]` selector on nav links (see AppShell.tsx)

export interface NavGroup {
  label: string;
  items: NavItem[];
}

export const TOP_NAV_ITEMS: NavItem[] = [
  { label: 'Dashboard', to: '/dashboard', icon: LayoutDashboard },
];

export const QR_NAV_ITEM: NavItem = { label: 'Scan QR', to: '/qr-scan', icon: QrCode };

/**
 * Grouped per requirements-v2.md's Modules table. Each v2 table doesn't get its
 * own top-level link — related tables are tabs within one page (see each page's
 * TabbedPage usage) so the primary nav stays a manageable ~15 items even though
 * the schema behind it spans ~70 tables. Page-vs-tab boundaries are documented
 * at the top of each page file.
 */
export const NAV_GROUPS: NavGroup[] = [
  {
    label: 'Farm & Assets',
    items: [
      { label: 'Farm Setup', to: '/farm', icon: Building2 },
      { label: 'Assets Registry', to: '/assets', icon: Boxes },
    ],
  },
  {
    label: 'Animals',
    items: [
      { label: 'Animals', to: '/animals', icon: PawPrint },
      { label: 'Cattle Register', to: '/cattle-register', icon: BookOpen },
      { label: 'Animal Lifecycle', to: '/animals/lifecycle', icon: GitBranch },
    ],
  },
  {
    label: 'Dairy',
    items: [{ label: 'Dairy', to: '/dairy', icon: Milk }],
  },
  {
    label: 'Breeding',
    items: [{ label: 'Breeding', to: '/breeding', icon: Baby }],
  },
  {
    label: 'Health',
    items: [{ label: 'Health', to: '/health', icon: HeartPulse }],
  },
  {
    label: 'Poultry',
    items: [{ label: 'Poultry', to: '/poultry', icon: ChickenIcon }],
  },
  {
    label: 'Coconut',
    items: [{ label: 'Coconut', to: '/coconut', icon: TreePalm }],
  },
  {
    label: 'Inventory',
    items: [{ label: 'Inventory', to: '/inventory', icon: Warehouse }],
  },
  {
    label: 'Procurement & Finance',
    items: [
      { label: 'Procurement & Finance', to: '/procurement-finance', icon: ShoppingCart },
      { label: 'Online Shop & Customers', to: '/online-shop', icon: Store },
    ],
  },
  {
    label: 'Operations',
    items: [
      { label: 'Tasks', to: '/tasks', icon: ClipboardList },
      { label: 'Enquiries', to: '/enquiries', icon: MessageSquare },
    ],
  },
  {
    label: 'Utilities',
    items: [{ label: 'Utilities', to: '/utilities', icon: Droplets }],
  },
  {
    label: 'Administration',
    items: [{ label: 'Administration', to: '/admin', icon: ShieldCheck }],
  },
];

export const REPORTS_NAV_ITEM: NavItem = { label: 'Reports', to: '/reports', icon: FileBarChart2 };

/**
 * Which permission module(s) gate each nav item's route — mirrors each
 * page's own backend MODULE constant(s) (see app/api/v1/*.py). Used by
 * AppShell to hide a nav item entirely when the signed-in user can't View
 * any of its modules (their Role never granted it, or an Admin/Super Admin
 * has restricted them via Page Access — see UserModuleAccess,
 * app/models/rbac.py) — rather than showing every page and letting each one
 * fail with "You do not have permission" after the click. A route with no
 * entry here is always shown (none currently — every nav-reachable route is
 * listed, including Dashboard/Reports/Scan QR).
 */
export const NAV_ITEM_MODULES: Record<string, string[]> = {
  // Dashboard is always in ALWAYS_INCLUDED_MODULES (ILMSPageAccessPicker.tsx)
  // so a restricted user always keeps somewhere to land after login, even
  // with every other page unchecked — gating it here (rather than leaving
  // it unconditional) means that's a deliberate, visible grant, not an
  // exemption from "only assigned pages".
  '/dashboard': ['Dashboard'],
  '/qr-scan': ['Animals'],
  '/reports': ['Finance Reports', 'Operational Reports'],
  '/farm': ['System Settings'],
  '/assets': ['Animals'],
  '/animals': ['Animals', 'Tags & QR', 'Animal Lifecycle', 'Movement'],
  '/animals/lifecycle': ['Animals'],
  '/cattle-register': ['Animals'],
  '/dairy': ['Milk'],
  '/breeding': ['Breeding', 'Pregnancy', 'Calving'],
  '/health': ['Health', 'Treatment', 'Vaccination', 'Weight'],
  '/poultry': ['Poultry'],
  '/coconut': ['Coconut'],
  '/inventory': ['Inventory'],
  '/procurement-finance': ['Purchases', 'Sales', 'Expenses'],
  '/online-shop': ['Sales', 'Deliveries'],
  '/tasks': ['Tasks'],
  '/enquiries': ['Enquiries'],
  '/utilities': ['Utilities'],
  // Notifications is deliberately excluded here even though Administration
  // hosts a Notification Rules tab: Notifications is also a baseline module
  // every restricted user keeps (see ALWAYS_INCLUDED_MODULES in
  // ILMSPageAccessPicker.tsx, so the notification bell keeps working), and
  // it's also part of nearly every seeded Role's daily-entry grants — using
  // it as an OR-trigger here would show the whole Administration page to
  // almost anyone.
  //
  // Users is excluded for the same reason, on top of the same problem:
  // Operator/User roles were given VIEW-only on Users so the Dairy
  // Collection Hub's "Name of the Operator" picklist can read usernames
  // (see seed.py's ROLE_RULES) — using Users as an OR-trigger here would
  // leak the whole Administration page to every Operator. Every real
  // Admin/Super Admin/Manager already reaches Administration via Roles &
  // Permissions/System Settings/Audit Logs, granted together as a set —
  // nobody is seeded with Users alone.
  '/admin': ['Roles & Permissions', 'System Settings', 'Audit Logs'],
};

/** Same check AppShell's sidebar uses to hide a nav item — exported so
 * Login.tsx can also use it to pick where to land a user who (per Page
 * Access) can't see Dashboard, without duplicating the module-matching
 * logic in two places. */
export function hasNavAccess(
  to: string,
  roles: string[],
  permissions: { module: string; action: string }[]
): boolean {
  const modules = NAV_ITEM_MODULES[to];
  if (!modules) return true;
  if (roles.includes('Super Admin')) return true;
  return modules.some((m) => permissions.some((p) => p.module === m && p.action === 'View'));
}

/** The full, ordered list of routes a signed-in user might land on — checked
 * top to bottom for the first one they can actually access. Used only as a
 * post-login fallback (see Login.tsx) when the usual default, /dashboard,
 * isn't one of their assigned pages. */
export function firstAccessibleRoute(roles: string[], permissions: { module: string; action: string }[]): string {
  const candidates = [
    ...TOP_NAV_ITEMS.map((i) => i.to),
    ...NAV_GROUPS.flatMap((g) => g.items.map((i) => i.to)),
    REPORTS_NAV_ITEM.to,
    QR_NAV_ITEM.to,
  ];
  return candidates.find((to) => hasNavAccess(to, roles, permissions)) ?? '/dashboard';
}
